import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { HistoryService } from '../../../services/history/history.service';
import { IoService } from '../../../services/io/io.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { MessageService } from '../../../services/message/message.service';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { AppComponent } from 'src/app/app.component';
import { ReportService } from 'src/app/services/report/report.service';
import { ClientSettingsService } from 'src/app/services/client-settings/client-settings.service';
import { RecordPaymentDialogComponent } from '../record-payment-dialog/record-payment-dialog.component';

@Component({
  selector: 'app-detail-purchase',
  templateUrl: './detail-purchase.component.html',
  styleUrls: ['./detail-purchase.component.scss']
})
export class DetailPurchaseComponent implements OnInit {

  documentId$: Observable<any>;
  documentId;

  showBackButton;

  purchase;

  conversationActive;
  conversationActiveUpdated: Subscription;

  permissionsLoaded: Subscription;
  permissions;

  sendingReceipt;
  receiptText = '';
  receiptError = false;
  // Emailed receipts go out FROM Location.email (CloudAPI receipt-c → email-c),
  // so Send Receipt is disabled until the purchase's location has one.
  locationEmail: string | null = null;
  locationEmailChecked = false;
  private static locationEmailByLocationId: { [locationId: string]: string } = {};

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private historyService: HistoryService,
    private ioService: IoService,
    private authorizationService: AuthorizationService,
    private messageService: MessageService,
    private reportService: ReportService,
    private clientSettingsService: ClientSettingsService,
  ) { }

  ngOnInit() {
    this.documentId$ = this.route.paramMap.pipe(switchMap(params => of(params.get('documentId'))));
    this.documentId$.subscribe(documentId => {
      if (documentId) {
        this.documentId = documentId;
        this.loadPurchaseDetail();
      }
    });

    if (this.historyService.getPreviousLinkDepth() > 3) {
      this.showBackButton = true;
    }

    this.conversationActiveUpdated = this.messageService.conversationActiveUpdatedObservable().subscribe(conversationActive => {
      this.conversationActive = conversationActive;
    });
    this.conversationActive = this.messageService.getConversationActive();
  }

  ngOnDestroy() {
    if (this.conversationActiveUpdated) {
      this.conversationActiveUpdated.unsubscribe();
    }
    if (this.permissionsLoaded) {
      this.permissionsLoaded.unsubscribe();
    }
  }

  close() {
    this.reportService.closeDetailPanel(this.route);
  }

  back() {
    this.location.back();
  }

  monitorPermissions() {
    if (this.authorizationService.permissionsAreLoaded()) {
      this.checkPermissions();
    } else {
      this.permissionsLoaded = this.authorizationService.permissionsLoadedObservable().subscribe(() => {
        this.checkPermissions();
      });
    }
  }

  checkPermissions() {

    this.permissions.canManageInventory = this.authorizationService.checkPermission('account.inventory.manage', this.purchase.locationId, false);
    this.permissions.canCancel = this.authorizationService.checkPermission('purchase', this.purchase.locationId);
    this.permissions.canReturn = this.authorizationService.checkPermission('return', this.purchase.locationId);
    this.permissions.canEdit = this.authorizationService.checkPermission('view.purchase', this.purchase.locationId);
    this.permissions.canDelete = this.authorizationService.checkPermission('purchase.delete', this.purchase.locationId);
    this.permissions.canArchive = this.authorizationService.checkPermission('purchase.archive', 'general', false);
    this.permissions.canSendReceipt = this.authorizationService.checkPermission('purchase.receipt', this.purchase.locationId);
    this.permissions.canComplete = this.authorizationService.checkPermission('purchase.complete', this.purchase.locationId)
                                   && this.purchase.status === 'open' && this.purchase.subtotal !== 0 && this.purchase.due === 0;
    // Admin-only: record a card payment taken outside the register (no charge is run).
    this.permissions.canRecordPayment = this.authorizationService.checkPermission('admin', this.purchase.locationId)
                                        && this.purchase.status === 'open' && this.purchase.due > 0
                                        && !this.purchase.pendingTransactions;
  }

  // Line items open their inventory transaction (View when the client config
  // names one, legacy detail-transaction otherwise — loadDetail handles that);
  // items with no matching transaction fall back to the item detail.
  openItemDetail(item) {
    if (item.inventoryTransactionId) {
      this.loadDetail('inventory-transaction', item.inventoryTransactionId);
    } else if (item.accountId) {
      this.loadDetail('inventory', item.accountId);
    }
  }

  loadDetail(type, id) {
    // Inventory transaction rows open the dynamic View when the CLIENT
    // configuration names one (settings.viewKeys.inventoryTransaction) AND the
    // user holds view.<key>.view somewhere. Client-level (not per-report, unlike
    // itemViewKey below) so it applies wherever a purchase detail renders.
    // Without the setting — every legacy client — behavior is unchanged.
    if (type === 'inventory-transaction') {
      const transactionViewKey = this.clientSettingsService.getViewKey('inventoryTransaction');
      if (transactionViewKey && this.authorizationService.checkPermission('view.' + transactionViewKey + '.view', 'any', false)) {
        this.reportService.navigateWithinReport(['../../details/' + transactionViewKey + '/' + id], this.route);
        return;
      }
      type = 'transaction';
    }
    // Line items open the dynamic View detail when the report's template opts
    // in via itemViewKey (Report doc config, mirroring PurchaseType.itemViewKey
    // on the transfer screen) AND the user holds view.<key>.view somewhere.
    // Without the key — every existing client — behavior is unchanged.
    if (type === 'inventory') {
      const itemViewKey = this.reportService.getActiveTemplate()?.itemViewKey;
      if (itemViewKey && this.authorizationService.checkPermission('view.' + itemViewKey + '.view', 'any', false)) {
        this.reportService.navigateWithinReport(['../../details/' + itemViewKey + '/' + id], this.route);
        return;
      }
    }
    if (type != 'inventory' || this.permissions.canManageInventory) {
      this.reportService.navigateWithinReport(['../../'+type+'/'+id], this.route);
    }
  }

  loadPurchaseDetail() {
    this.receiptText = '';
    return new Promise((resolve, reject) => {
      if (this.permissionsLoaded) {
        this.permissionsLoaded.unsubscribe();
      }
      this.permissions = {
        canCancel: false,
        canReturn: false,
        canEdit: false
      };
      this.purchase = null;
      this.ioService.post('/purchase/getPurchase', {
        purchaseId: this.documentId,
        detail: true,
      }).then((purchase: any) => {

        let transactions = [];

        let inventoryTransactions = [];

        let taxTransactions = [];

        let discountTransactions = [];

        let purchaseDiscounts = {};


        let addValues = [];
        let pendingTransactions = false;

        // Corrections ride in the same purchaseId-linked list. Flag each
        // corrected transaction so both sides of the pair are obvious.
        const correctedIds = new Set(
          purchase.transactions.filter(t => t.correctionForId).map(t => String(t.correctionForId)));
        purchase.hasCorrections = purchase.transactions.some(t => t.correction);
        for (const t of purchase.transactions) {
          t.hasCorrections = correctedIds.has(String(t._id));
        }

        for (let transaction of purchase.transactions) {
          if (transaction.type == 'gift' || transaction.type == 'Gift') {
            transaction.cardType = 'Gift Card';
          }

          if (transaction.type == 'cash') {
            transaction.cardType = 'Cash';
          }

          if (transaction.type == 'check') {
            transaction.cardType = 'Check';
          }

          if (transaction.type == 'invoice') {
            transaction.cardType = 'Invoice';
          }

          if (transaction.type == 'reward') {
            transaction.cardType = 'Reward';
          }

          if (transaction.type == 'billingAccount') {
            transaction.cardType = 'Billing Account';
            if (transaction.accountName) {
              transaction.cardType = transaction.accountName;
            }
          }

          if (transaction.type == 'credit') {
            transaction.cardType = 'Credit';
            if (transaction.cardBrand) {
              transaction.cardType = transaction.cardBrand;
            }
          }

          if (transaction.status == 'pending') {
            pendingTransactions = true;
          }

          switch (transaction.type) {
            case 'cash':
            case 'gift':
            case 'check':
            case 'credit':
            case 'invoice':
            case 'reward':
            case 'billingAccount':
              transactions.push(transaction);
              break;

            case 'inventory':
              inventoryTransactions.push(transaction);
              break;

            case 'discount':
              discountTransactions.push(transaction);
              break;

            case 'tax':
              taxTransactions.push(transaction);
              break;
          }

          // if (transaction.amount <= 0 && !transaction.omit) {
          //   transactions.push(transaction);
          // } else {
          //   if (transaction.type == 'gift' || transaction.type == 'Gift') {
          //     addValues.push(transaction);
          //   }
          // }


        }

        

        if (purchase.items && purchase.items.length > 0) {
          for (let item of purchase.items) {
            item.discountsArray = [];
            if (item.discounts) {
              // Loop through item.discounts object keys
              for (let discountId in item.discounts) {
                if (item.discountsUiDisplayOnItem && item.discountsUiDisplayOnItem[discountId]) {
                  item.discountsArray.push({
                    id: discountId,
                    name: item.discountsUiName[discountId],
                    value: item.discountsUi[discountId]
                  });
                  
                } else {

                  try {
                    if (!purchaseDiscounts[discountId]) {
                      purchaseDiscounts[discountId] = {
                        name: '',
                        value: 0
                      };
                    }

                    if (item.discountsUiName && item.discountsUiName[discountId]) {
                      purchaseDiscounts[discountId].name = item.discountsUiName[discountId];
                    }
                    
                    // purchaseDiscounts[discountId].value += item.discounts[discountId];

                  } catch (e) {
                    console.log('Error processing discount', e);
                  }

                }
              }
            }
          }
        }

        // console.log('purchaseDiscounts', purchaseDiscounts);
        purchase.discountsUi = [];
        for (let discountId in purchaseDiscounts) {
          purchase.discountsUi.push({
            id: discountId,
            name: purchaseDiscounts[discountId].name,
            value: purchase.discounts[discountId]
          });
        }

        // console.log('purchase.discountsUi', purchase.discountsUi);

        purchase.pendingTransactions = pendingTransactions;
        purchase.transactions = transactions;

        purchase.inventoryTransactions = inventoryTransactions;

        // Map each line item to its inventory transaction: the nth line for an
        // account pairs with the nth NON-correction inventory transaction for
        // that account (same-product lines are interchangeable, so order-based
        // pairing within an account is sound; corrections must not consume a
        // slot). Items without a match — open purchases whose inventory hasn't
        // transacted yet, or panels loaded without purchase.detail — keep the
        // legacy item link via openItemDetail's fallback.
        const itemTxByAccount: { [accountId: string]: any[] } = {};
        for (const tx of inventoryTransactions) {
          if (tx.correction || !tx.accountId) { continue; }
          const key = String(tx.accountId);
          (itemTxByAccount[key] = itemTxByAccount[key] || []).push(tx);
        }
        for (const list of Object.values(itemTxByAccount)) {
          list.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
        }
        for (const item of (purchase.items || [])) {
          const list = item.accountId ? itemTxByAccount[String(item.accountId)] : null;
          item.inventoryTransactionId = (list && list.length) ? String(list.shift()._id) : null;
        }
        purchase.discountTransactions = discountTransactions;
        purchase.taxTransactions = taxTransactions;

        purchase.addValues = addValues;

        if (!purchase.due) {
          purchase.due = 0;
        }

        this.purchase = purchase;
        console.log(this.purchase);

        this.loadLocationEmail(purchase.locationId);
        this.monitorPermissions();
      });
    });
  }

  cancelPurchase() {
    console.log('cancel purchase');
    this.ioService.post('/purchase/cancel', {
      purchaseId: this.documentId
    }).then((cancelResponse: any) => {
      this.loadPurchaseDetail();
    }).catch(err => {

    });
  }

  // True once the location was looked up and has no usable sender email: the
  // receipt input + button are then not rendered at all. An unknown state
  // (lookup pending or failed) leaves them shown — the server still validates
  // and the error is surfaced by sendReceipt().
  get sendReceiptDisabled(): boolean {
    return this.locationEmailChecked && !this.locationEmail;
  }

  loadLocationEmail(locationId: string) {
    this.locationEmail = null;
    this.locationEmailChecked = false;
    if (!locationId) {
      return;
    }
    const key = String(locationId);
    const cached = DetailPurchaseComponent.locationEmailByLocationId[key];
    if (cached !== undefined) {
      this.locationEmail = cached;
      this.locationEmailChecked = true;
      return;
    }
    this.ioService.post('/location/getLocation', { locationId: key }).then((location: any) => {
      // Imported locations can hold an array or whitespace here; treat those as empty.
      const email = location && typeof location.email === 'string' ? location.email.trim() : '';
      DetailPurchaseComponent.locationEmailByLocationId[key] = email;
      if (this.purchase && String(this.purchase.locationId) === key) {
        this.locationEmail = email;
        this.locationEmailChecked = true;
      }
    }).catch((err) => {
      console.log('loadLocationEmail error: ', err);
    });
  }

  sendReceipt() {
    if (this.sendReceiptDisabled) {
      return;
    }
    if (!this.sendingReceipt) {
      LoadingComponent.showLoading();
      this.sendingReceipt = true;
      this.receiptError = false;
      this.receiptText = '';
      this.ioService.post('/receipt/send', {
        purchaseId: this.purchase._id,
        email: this.purchase.email
      }).then((receiptResponse: any) => {
        console.log('receiptResponse: ', receiptResponse);
        this.receiptText = 'Receipt Sent Successfully';
      })
      .catch((err) => {
        // The API answers 403 with either email-c's string array (e.g.
        // ["From email is required."]) or receipt-c's { msg }. Surface it —
        // a silent failure here hid missing location config for months.
        console.error('sendReceipt error: ', err);
        this.receiptError = true;
        this.receiptText = 'Receipt not sent: ' + this.describeReceiptError(err);
        AppComponent.showSnack(this.receiptText, 'OK', 10000);
      })
      .finally(() => {
        this.sendingReceipt = false;
        LoadingComponent.hideLoading();
      });
    }
  }

  // Turns a /receipt/send failure into a message that says what to fix.
  private describeReceiptError(err: any): string {
    if (err && err.status === 0) {
      return 'no connection to the server.';
    }
    const body = err && err.error !== undefined ? err.error : err;
    let message = '';
    if (Array.isArray(body)) {
      message = body.join(' ');
    } else if (body && typeof body === 'object') {
      message = body.msg || body.message || '';
    } else if (typeof body === 'string') {
      message = body;
    }
    const locationName = this.purchase && this.purchase.locationName ? this.purchase.locationName : 'this location';
    if (/from email/i.test(message)) {
      return `${locationName} has no valid sender email. Set the Email field on the location (Locations report) and try again.`;
    }
    if (/receipt template/i.test(message)) {
      return `${locationName} has no email receipt template configured (Location receipt.templateId).`;
    }
    if (/valid email address/i.test(message)) {
      return 'enter a valid recipient email address.';
    }
    if (/permission/i.test(message)) {
      return 'you do not have permission to send receipts at ' + locationName + '.';
    }
    return message || ('the server returned status ' + (err && err.status !== undefined ? err.status : 'unknown') + '.');
  }

  deletePurchase() {
    console.log('deletePurchase');
  }

  archivePurchase = async () => {
    console.log('archivePurchase');

    // Verify if the user really wants to archive
    if (!confirm('Are you sure you want to archive this purchase? This action cannot be undone.')) {
      return;
    }

    try {
      let archiveResponse: any =  await this.ioService.post('purchase/archivePurchase', {
        purchaseId: this.purchase._id
      });

      AppComponent.showSnack(archiveResponse.msg, 'OK', 3000);
    } catch (err) {
      console.error('Error archiving purchase:', err);
      AppComponent.showSnack(err.error.msg, 'OK', 3000);
    }
    this.reportService.loadReport(null);
    this.reportService.closeDetailPanel(this.route);


  }

  recordPayment() {
    AppComponent.dialog.open(RecordPaymentDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'opaque-dialog',
      data: { purchaseId: this.purchase._id },
    }).afterClosed().subscribe((result: any) => {
      if (result && result.recorded) {
        this.reportService.loadReport(null);
        if (result.purchaseStatus === 'complete') {
          this.reportService.closeDetailPanel(this.route);
        } else {
          this.loadPurchaseDetail();
        }
      }
    });
  }

  async completePurchase() {
    await this.ioService.post('purchase/completePurchase', { purchaseId: this.purchase._id } )
    .then( (response: any) => {
      this.reportService.loadReport(null);
      this.reportService.closeDetailPanel(this.route);
      AppComponent.showSnack( 'Purchase is ' + response.purchaseStatus, 'OK', 3000 );
    });
  }
}
